/**
 * Every audit in the firm, in one table, for one module. Under a client
 * (`/clients/3/modules/o2c/audits`, the Audits card on the client's hub) it
 * opens filtered to that client: Clients › {client} › {Module} › Audits.
 */

import { auditPath, auditsCrumbs } from '../modules/flow';
import { useModule } from '../modules/useModule';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Card, StatusBadge } from '../../shared/ui';
import { useAuditsQuery } from '../../queries/audits';
import { useClientsQuery } from '../../queries/clients';
import { count, date, dateTime } from '../../utils/format';
import { financialYearLabel } from '../../utils/period';
import { auditorStatus } from '../../shared/roles';
import {
	DataTable,
	FilterBar,
	useFullScreen,
	type Column,
	type FilterSpec,
} from '../shared/tables';
import { EmptyState, ErrorState, LoadingState } from '../shared/states';
import { PageHeader } from '../../provider/pageTitle';
import type { AuditSummary } from '../../schemas';
import { Button } from '@mui/material';

/**
 * The status chips, in the order work moves through them.
 *
 * Not alphabetical and not by count: a reader scanning this row is asking
 * "where is everything up to", and the answer reads left to right.
 */
const STATUS_CHIPS: Array<{ value: string; label: string }> = [
	{ value: 'PREPARING_DATA', label: 'Preparing' },
	{ value: 'IN_PROGRESS', label: 'In progress' },
	{ value: 'UNDER_REVIEW', label: 'Report drafted' },
	{ value: 'REPORTED', label: 'Issued' },
];

/** The FY a period start falls in, read straight off the ISO string so there
 *  is no `Date`/timezone shift between the boundary a report cites and the
 *  filter option it groups under (see `utils/format.ts`'s `date()` for the
 *  same concern). */
function periodFYLabel(periodStart: string): string | null {
	const m = /^(\d{4})-(\d{2})/.exec(periodStart);
	if (!m) return null;
	const year = Number(m[1]);
	const month = Number(m[2]);
	return financialYearLabel(month >= 4 ? year : year - 1);
}

export default function AuditsList() {
	const [params, setParams] = useSearchParams();
	const { clientId: pathClientId } = useParams();
	const fullScreen = useFullScreen('Audits');
	const [filters, setFilters] = useState<Record<string, string>>({
		client: pathClientId ?? params.get('client') ?? '',
		status: '',
		period: '',
		search: '',
	});
	const sortDir = 'desc' as 'asc' | 'desc';

	const { module, link } = useModule();
	const audits = useAuditsQuery(module);
	const clients = useClientsQuery(module);
	const rows = audits.data ?? [];

	/* A client chosen in one module is not a filter in another it is not set
     up for: once this module's clients are known, drop it (and `?client=`). */
	useEffect(() => {
		if (!filters.client || !clients.data) return;
		if (clients.data.some((client) => client.pkClientId === filters.client))
			return;
		setFilters((current) => ({ ...current, client: '' }));
		setParams(
			(current) => {
				const next = new URLSearchParams(current);
				next.delete('client');
				return next;
			},
			{ replace: true },
		);
	}, [clients.data, filters.client, setParams]);

	const filtered = useMemo(() => {
		const search = (filters.search ?? '').trim().toLowerCase();
		const matched = rows.filter((row) => {
			if (filters.client && row.fkClientId !== filters.client)
				return false;
			if (filters.status && auditorStatus(row.status) !== filters.status)
				return false;
			if (
				filters.period &&
				periodFYLabel(row.periodStart) !== filters.period
			)
				return false;
			if (
				search &&
				!`${row.name} ${row.clientName} ${row.clientCode}`
					.toLowerCase()
					.includes(search)
			) {
				return false;
			}
			return true;
		});
		/* "Newest / Oldest first" sorts by when the audit was created — the one
       recency the API gives us (`createdAt`; there is no `updatedAt` on
       `AuditSummary`). Done here, ahead of the table, rather than through
       `DataTable`'s own column sort: a reader who then clicks a column
       header gets that order instead, the same trade-off every sortable
       table in this app makes. */
		return [...matched].sort((a, b) =>
			sortDir === 'desc'
				? b.createdAt.localeCompare(a.createdAt)
				: a.createdAt.localeCompare(b.createdAt),
		);
	}, [rows, filters, sortDir]);

	const periodOptions = useMemo(() => {
		const labels = new Set(
			rows.map((row) => periodFYLabel(row.periodStart)).filter(Boolean),
		);
		return [
			{ value: '', label: 'All periods' },
			...Array.from(labels)
				.sort((a, b) => (b as string).localeCompare(a as string))
				.map((label) => ({
					value: label as string,
					label: label as string,
				})),
		];
	}, [rows]);

	const summary = useMemo(() => {
		const byStatus: Record<string, number> = {};
		for (const row of rows) {
			const grouped = auditorStatus(row.status);
			byStatus[grouped] = (byStatus[grouped] ?? 0) + 1;
		}
		return { all: rows.length, byStatus };
	}, [rows]);

	const specs: FilterSpec[] = [
		{
			key: 'status',
			label: 'Status',
			kind: 'chips',
			/* A status no audit is in cannot usefully be chosen, so it is not offered. */
			options: [
				{ value: '', label: `All ${count(summary.all)}` },
				...STATUS_CHIPS.filter(
					(option) => (summary.byStatus[option.value] ?? 0) > 0,
				).map((option) => ({
					value: option.value,
					label: `${option.label} ${count(summary.byStatus[option.value] ?? 0)}`,
				})),
			],
		},
		{
			key: 'client',
			label: 'Client',
			kind: 'select',
			options: [
				{ value: '', label: 'All clients' },
				...(clients.data ?? []).map((client) => ({
					value: client.pkClientId,
					label: client.name,
				})),
			],
		},
		{
			key: 'period',
			label: 'Period',
			kind: 'select',
			options: periodOptions,
		},
		{
			key: 'search',
			label: 'Search',
			kind: 'search',
			placeholder: 'Audit, client or code',
		},
	];

	const columns = useMemo<Array<Column<AuditSummary>>>(
		() => [
			{
				key: 'audit',
				label: 'Audit',
				width: 200,
				sortValue: (row) => row.name,
				render: (row) => (
					<Link to={auditPath(row)} className="link b">
						{row.name}
					</Link>
				),
			},
			{
				key: 'client',
				label: 'Client',
				width: 150,
				sortValue: (row) => row.clientName,
				render: (row) => row.clientName,
			},
			{
				key: 'period',
				label: 'Period',
				width: 150,
				sortValue: (row) => row.periodStart,
				render: (row) => (
					<span className="nowrap">
						{date(row.periodStart)} – {date(row.periodEnd)}
					</span>
				),
			},
			/* No Files column. How many files an engagement has is a fact about
         intake's work, on a list belonging to the half of the product that
         does none of it — see the note in the approved source. */
			/* Open exceptions hidden while the Exceptions tab is.
      {
        key: 'exceptions',
        label: 'Open exceptions',
        num: true,
        width: 110,
        sortValue: (row) => row.openExceptions,
        render: (row) => (
          <>
            {count(row.openExceptions)}
            {row.blockingExceptions > 0 && (
              <small className="mut"> · {count(row.blockingExceptions)} blocking</small>
            )}
          </>
        ),
      },
      */
			{
				key: 'createdAt',
				label: 'Created at',
				width: 140,
				sortValue: (row) => row.createdAt,
				render: (row) => (
					<span className="nowrap">{dateTime(row.createdAt)}</span>
				),
			},
			{
				key: 'createdBy',
				label: 'Created by',
				width: 140,
				sortValue: (row) => row.createdByName ?? '',
				render: (row) => row.createdByName ?? '—',
			},
			{
				key: 'updatedAt',
				label: 'Last updated at',
				width: 150,
				sortValue: (row) => row.lastEditedAt ?? '',
				render: (row) => (
					<span className="nowrap">{dateTime(row.lastEditedAt)}</span>
				),
			},
			{
				key: 'updatedBy',
				label: 'Last updated by',
				width: 140,
				sortValue: (row) => row.lastEditedByName ?? '',
				render: (row) => row.lastEditedByName ?? '—',
			},
			{
				key: 'status',
				label: 'Status',
				width: 130,
				sortValue: (row) => auditorStatus(row.status),
				/* Grouped, the way the approved screen's own column reads it — see
           the file header. `StatusBadge` falls back to a humanised label for
           a value the `auditStatus` vocabulary group does not carry (the
           three synthetic groups this function invents), so `PREPARING_DATA`
           reads "Preparing data" and `REPORTED` reads "Reported" without the
           API needing to know either name. */
				render: (row) => (
					<StatusBadge group="auditStatus" value={row.status} />
				),
			},
		],
		[link],
	);

	const loading = audits.isPending;
	const error = audits.error;

	const chosenClient = (clients.data ?? []).find(
		(client) => client.pkClientId === filters.client,
	);

	return (
		<div className="stack">
			<PageHeader
				/* Reached from a client's hub (the sidebar has no Audits entry):
           Clients › {client} › {Module} › Audits, each a way back. */
				breadcrumbs={
					chosenClient
						? auditsCrumbs(
								{
									id: chosenClient.pkClientId,
									name: chosenClient.name,
								},
								module,
							)
						: [{ label: 'Clients', to: '/clients' }]
				}
				title="Audits"
				subtitle={
					chosenClient
						? [chosenClient.code, chosenClient.gstin]
								.filter(Boolean)
								.join(' · ') || undefined
						: undefined
				}
				actions={
					<Button
						component={Link}
						variant="contained"
						to={link(
							`/audits/new${filters.client ? `?client=${filters.client}` : ''}`,
						)}
					>
						New audit
					</Button>
				}
			/>

			<FilterBar
				specs={specs}
				values={filters}
				onChange={(key, value) =>
					setFilters((current) => ({ ...current, [key]: value }))
				}
				onClear={() =>
					setFilters({
						client: '',
						status: '',
						period: '',
						search: '',
					})
				}
				resultNote={
					!loading && !error
						? `${count(filtered.length)} audits`
						: undefined
				}
				actions={fullScreen.button}
			/>

			<ErrorState error={error} onRetry={audits.refetch} />

			{loading ? (
				<LoadingState label="Loading audits…" />
			) : !error && filtered.length === 0 ? (
				<EmptyState
					title={
						rows.length === 0
							? 'No audits yet'
							: 'No audits match these filters'
					}
					action={
						rows.length === 0 ? (
							<Button
								component={Link}
								variant="contained"
								to={link('/audits/new')}
							>
								New audit
							</Button>
						) : (
							<Button
								variant="outlined"

								onClick={() =>
									setFilters({
										client: '',
										status: '',
										period: '',
										search: '',
									})
								}
							>
								Clear filters
							</Button>
						)
					}
				>
					{rows.length === 0
						? 'Create an audit for a client and period.'
						: 'Try clearing a filter.'}
				</EmptyState>
			) : !error ? (
				<Card className="mt0">
					<div className="card-bd flush">
						<DataTable
							rows={filtered}
							columns={columns}
							rowKey={(row) => row.pkAuditId}
							caption="Audits"
							fullScreen={fullScreen}
							maxHeight="main"
						/>
					</div>
				</Card>
			) : null}
		</div>
	);
}
